/**
 * Quiz round renderer used by LobbyGame for `round.mode === "quiz"`.
 * - Medium: 2x2 grid of 4 option buttons (click to select, Enter / Lock-in to commit).
 * - Hard / Insane: free-text input keyed by `answer_type`
 *      player → server-side search via /api/players/search
 *      team   → /api/teams/search
 *      country → reuse <CountryPicker> (typeahead + aliases)
 *      year / number → numeric input
 *      position → fixed list of position labels.
 *
 * `lockedAnswer` is `null` until submitted, then `{ kind, text, optionIndex? }`.
 * Enter submits (when something is selected/typed). The component is remounted
 * each round via `key={round.round_number}` in the parent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import CountryPicker from "./CountryPicker";
import { quizCategoryDisplay } from "../lib/quiz_categories";

const POSITION_OPTIONS = ["Goalkeeper", "Defender", "Midfielder", "Forward"];

function LockedChip({ text }) {
  return (
    <motion.div
      initial={{ scale: 0.97, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      data-testid="quiz-answer-locked"
      className="rounded-2xl border-2 border-[var(--accent)] bg-[var(--accent)]/10 px-4 py-3.5 flex items-center gap-3"
    >
      <div className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center">
        <Check size={18} strokeWidth={3} className="text-black" />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">answer locked</div>
        <div className="font-bold text-[var(--accent)]">{text || "submitted"}</div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">cannot change</div>
    </motion.div>
  );
}

function MultipleChoice({ options, disabled, locked, onSubmit }) {
  const [sel, setSel] = useState(null);

  const commit = () => {
    if (sel == null || disabled || locked) return;
    onSubmit({ kind: "mc", optionIndex: sel, text: options[sel]?.text });
  };

  // Enter to commit when an option is selected.
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Enter" && sel != null && !locked) {
        e.preventDefault();
        commit();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [sel, locked, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (locked) return <LockedChip text={locked.text} />;

  return (
    <div data-testid="quiz-mc-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((o, i) => {
        const picked = sel === i;
        return (
          <button
            key={i}
            type="button"
            disabled={disabled || locked}
            onClick={() => setSel(i)}
            onDoubleClick={() => { setSel(i); onSubmit({ kind: "mc", optionIndex: i, text: o.text }); }}
            data-testid={`quiz-mc-option-${i}`}
            className={`text-left px-5 py-5 rounded-2xl border-2 transition text-base font-semibold
              ${picked
                ? "border-[var(--accent)] bg-[var(--accent)]/10 shadow-[0_0_24px_-8px_var(--accent)]"
                : "border-white/10 hover:border-white/30 hover:bg-white/[0.04]"}
              disabled:opacity-40`}
          >
            <span className={`block text-[10px] uppercase tracking-widest mb-1 ${picked ? "text-[var(--accent)]" : "text-zinc-500"}`}>
              option {String.fromCharCode(65 + i)}
            </span>
            {o.text}
          </button>
        );
      })}
      <div className="sm:col-span-2 flex items-center justify-between gap-3 mt-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">
          {sel == null ? "Tap to select · double-click for instant lock-in" : "Press enter to lock in"}
        </span>
        <button
          type="button"
          disabled={sel == null || disabled || locked}
          onClick={commit}
          data-testid="quiz-mc-lock"
          className="px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-bold uppercase tracking-widest text-xs disabled:opacity-40"
        >
          Lock in
        </button>
      </div>
    </div>
  );
}

function NameAutocomplete({ kind, disabled, locked, onSubmit, placeholder }) {
  // kind = "player" | "team"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);  // {_id, name}
  const [active, setActive] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim() || disabled || picked) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const path = kind === "player" ? "/players/search" : "/teams/search";
        const { data } = await api.get(`${path}?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data);
        setActive(0);
      } catch { setResults([]); }
    }, 150);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, kind, disabled, picked]);

  const commit = () => {
    if (locked || disabled) return;
    if (picked) { onSubmit({ kind, text: picked.name, id: picked._id }); return; }
    const txt = q.trim();
    if (txt) onSubmit({ kind, text: txt });
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      if (open && results.length > 0 && !picked) {
        e.preventDefault();
        setPicked(results[active]);
        setQ("");
        setOpen(false);
        // Auto-commit on Enter after selecting from list (universal Enter-to-lock).
        const r = results[active];
        if (r) onSubmit({ kind, text: r.name, id: r._id });
        return;
      }
      if (picked || q.trim()) { e.preventDefault(); commit(); }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (locked) return <LockedChip text={locked.text} />;

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search size={16} className="text-zinc-600" />
          </div>
          <input
            data-testid="quiz-name-input"
            type="text"
            disabled={disabled}
            value={picked ? picked.name : q}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setPicked(null); setQ(e.target.value); setOpen(true); }}
            placeholder={placeholder}
            className="w-full pl-11 pr-12 py-3.5 rounded-2xl bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none disabled:opacity-40 text-base"
          />
          {(picked || q) && (
            <button
              type="button"
              onClick={() => { setPicked(null); setQ(""); }}
              data-testid="quiz-name-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Clear answer"
            >
              <X size={16} />
            </button>
          )}
          <AnimatePresence>
            {open && !picked && results.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="absolute z-20 mt-2 w-full max-h-72 overflow-auto rounded-2xl border border-white/10 bg-[var(--surface)] shadow-2xl"
              >
                {results.map((r, i) => (
                  <button
                    key={r._id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      setPicked(r); setQ(""); setOpen(false);
                      onSubmit({ kind, text: r.name, id: r._id });
                    }}
                    data-testid={`quiz-name-result-${i}`}
                    className={`w-full text-left px-4 py-2.5 text-sm transition ${i === active ? "bg-[var(--accent)]/10 text-[var(--accent)]" : "text-zinc-200 hover:bg-white/5"}`}
                  >
                    {r.name}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          type="button"
          disabled={disabled || locked || !(picked || q.trim())}
          onClick={commit}
          data-testid="quiz-lock-btn"
          className="px-5 rounded-2xl bg-[var(--accent)] text-black font-bold uppercase tracking-widest text-xs disabled:opacity-40"
        >
          Lock in
        </button>
      </div>
    </div>
  );
}

function NumericInput({ kind, disabled, locked, onSubmit, placeholder, minVal, maxVal }) {
  const [v, setV] = useState("");
  const commit = () => {
    if (locked || disabled) return;
    const s = v.trim();
    if (!s) return;
    onSubmit({ kind, text: s });
  };
  const onKey = (e) => {
    if (e.key === "Enter" && v.trim()) { e.preventDefault(); commit(); }
  };
  if (locked) return <LockedChip text={locked.text} />;
  return (
    <div className="flex gap-2">
      <input
        data-testid="quiz-numeric-input"
        type="number"
        inputMode="numeric"
        disabled={disabled}
        value={v}
        min={minVal}
        max={maxVal}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className="flex-1 px-4 py-3.5 rounded-2xl bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none disabled:opacity-40 text-base tabular-nums"
      />
      <button
        type="button"
        disabled={disabled || !v.trim() || locked}
        onClick={commit}
        data-testid="quiz-lock-btn"
        className="px-5 rounded-2xl bg-[var(--accent)] text-black font-bold uppercase tracking-widest text-xs disabled:opacity-40"
      >
        Lock in
      </button>
    </div>
  );
}

function PositionPicker({ disabled, locked, onSubmit }) {
  const [sel, setSel] = useState(null);
  if (locked) return <LockedChip text={locked.text} />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {POSITION_OPTIONS.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          onClick={() => { setSel(p); onSubmit({ kind: "position", text: p }); }}
          data-testid={`quiz-position-${p.toLowerCase()}`}
          className={`px-4 py-4 rounded-2xl border-2 text-sm font-bold uppercase tracking-widest transition
            ${sel === p ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]" : "border-white/10 hover:bg-white/5"}
            disabled:opacity-40`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function CountryAnswer({ disabled, locked, onSubmit }) {
  const [code, setCode] = useState("");
  if (locked) return <LockedChip text={locked.text} />;
  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1">
        <CountryPicker
          value={code}
          onChange={(c) => { setCode(c); onSubmit({ kind: "country", text: c, id: c }); }}
          testId="quiz-country"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default function QuizSection({ round, locked, onSubmit, remainingSec }) {
  const disabled = remainingSec <= 0;
  const answer_type = round.answer_type;
  const difficulty = round.difficulty;
  const opts = useMemo(() => round.options || [], [round.options]);

  const card = (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="quiz-question"
      className="rounded-3xl border border-white/10 bg-gradient-to-b from-[var(--surface)] to-[var(--surface-2)] px-6 sm:px-8 py-6 sm:py-8 text-center"
    >
      <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent)] mb-2 inline-flex items-center gap-1.5 justify-center">
        <span>{quizCategoryDisplay(round.picked_category_key || round.category).icon}</span>
        <span>
          {round.picked_category_display_name
            || quizCategoryDisplay(round.category).name
            || (round.category || "trivia").replace(/_/g, " ")}
        </span>
      </div>
      <h2 className="text-2xl sm:text-3xl font-black leading-tight">{round.question_text}</h2>
    </motion.div>
  );

  let body = null;
  if (opts.length === 4) {
    body = <MultipleChoice options={opts} disabled={disabled} locked={locked} onSubmit={onSubmit} />;
  } else if (answer_type === "player") {
    body = <NameAutocomplete kind="player" disabled={disabled} locked={locked} onSubmit={onSubmit}
                              placeholder="Type the player name…" />;
  } else if (answer_type === "team") {
    body = <NameAutocomplete kind="team" disabled={disabled} locked={locked} onSubmit={onSubmit}
                              placeholder="Type the club / national team…" />;
  } else if (answer_type === "country") {
    body = <CountryAnswer disabled={disabled} locked={locked} onSubmit={onSubmit} />;
  } else if (answer_type === "position") {
    body = <PositionPicker disabled={disabled} locked={locked} onSubmit={onSubmit} />;
  } else if (answer_type === "year") {
    body = <NumericInput kind="year" disabled={disabled} locked={locked} onSubmit={onSubmit}
                          placeholder="e.g. 2003" minVal={1900} maxVal={2030} />;
  } else if (answer_type === "number") {
    body = <NumericInput kind="number" disabled={disabled} locked={locked} onSubmit={onSubmit}
                          placeholder="Enter a number" minVal={0} maxVal={50} />;
  } else {
    body = <NameAutocomplete kind="player" disabled={disabled} locked={locked} onSubmit={onSubmit}
                              placeholder="Type your answer…" />;
  }

  return (
    <div className="space-y-5" data-testid="quiz-section">
      {card}
      {body}
    </div>
  );
}
